// Shared model + helpers for the tree-shaped importers (Devolutions RDM, mRemoteNG). Both parse a
// vendor file into the same `{ nodes, accesses }` shape and feed ImportDialog.runRdm, so the model,
// the `\`-delimited path helpers, the field helper, and the folder-depth collapse live here once.

import type { Field } from "./items";
import type { DeviceMeta } from "./devices";

export const MAX_DEPTH = 5; // matches the backend folder-depth limit

/** A folder/device tree node, keyed by its full `\`-delimited group path. */
export interface ImportNode {
  path: string;
  name: string;
  parentPath: string | null;
  kind: "folder" | "device";
  meta?: DeviceMeta;
}

/** An access method to create as an item under `groupPath`. */
export interface ImportAccess {
  type: string;
  name: string;
  notes?: string;
  fields: Field[];
  groupPath: string;
  /** Source connection ID — used to merge credentials (e.g. the RDM CSV export) by exact match. */
  id?: string;
}

export interface ImportTree {
  nodes: ImportNode[];
  accesses: ImportAccess[];
  counts: { folders: number; devices: number; accesses: number };
}

/** Split a `\`-delimited group path into its non-empty segments. */
export const segments = (path: string): string[] => path.split("\\").filter((s) => s.length > 0);

/** The parent path of a `\`-delimited path, or null at the top level. */
export const parentOf = (path: string): string | null => {
  const segs = segments(path);
  return segs.length > 1 ? segs.slice(0, -1).join("\\") : null;
};

/** Depth (number of segments) of a group path. */
export const pathDepth = (path: string): number => segments(path).length;

const SECRET_FIELDS = new Set(["password", "secret", "passphrase", "psk"]);

/** Build an access field, marking known credential types as secret. */
export const fld = (key: string, label: string, type: Field["type"], value: string): Field => ({
  key,
  label,
  type,
  value,
  secret: SECRET_FIELDS.has(type),
});

/** Enforce the backend depth limit: collapse nodes deeper than MAX_DEPTH into their deepest allowed
 *  ancestor, folding the overflow into the node name so nothing is lost. Mutates each access's
 *  `groupPath` in place and preserves device kind/meta. Returns the (possibly reduced) node list. */
export function collapseDepth(nodes: ImportNode[], accesses: ImportAccess[]): ImportNode[] {
  const tooDeep =
    nodes.some((n) => segments(n.path).length > MAX_DEPTH) ||
    accesses.some((a) => segments(a.groupPath).length > MAX_DEPTH);
  if (!tooDeep) return nodes;

  const remap = new Map<string, string>(); // original path → effective (collapsed) path
  const effective = (path: string): string => {
    if (!path) return path;
    const cached = remap.get(path);
    if (cached !== undefined) return cached;
    const segs = segments(path);
    if (segs.length <= MAX_DEPTH) {
      remap.set(path, path);
      return path;
    }
    const eff = [...segs.slice(0, MAX_DEPTH - 1), segs.slice(MAX_DEPTH - 1).join(" / ")].join("\\");
    remap.set(path, eff);
    return eff;
  };

  const collapsed = new Map<string, ImportNode>();
  for (const n of nodes) {
    const eff = effective(n.path);
    const segs = segments(eff);
    const merged: ImportNode =
      collapsed.get(eff) ?? { path: eff, name: segs[segs.length - 1] || n.name, parentPath: parentOf(eff), kind: "folder" };
    if (n.kind === "device") {
      merged.kind = "device";
      merged.meta = n.meta;
    }
    collapsed.set(eff, merged);
  }
  for (const a of accesses) a.groupPath = effective(a.groupPath);
  return [...collapsed.values()];
}

/** Collapse to the backend depth limit, then tally counts → a ready-to-import tree. */
export function buildImportTree(nodes: ImportNode[], accesses: ImportAccess[]): ImportTree {
  const collapsedNodes = collapseDepth(nodes, accesses);
  return {
    nodes: collapsedNodes,
    accesses,
    counts: {
      folders: collapsedNodes.filter((n) => n.kind === "folder").length,
      devices: collapsedNodes.filter((n) => n.kind === "device").length,
      accesses: accesses.length,
    },
  };
}
